/**
 * Team settings — /w/:slug/app/settings/team.
 *
 * See docs/users-onboarding-billing-plan.md §4.2 & §4.4.
 *
 * Sections:
 *   1. Member list — name/email/role/joined-at. Role dropdowns
 *      (Owner-only), Remove buttons (Owner/Admin, not-self).
 *   2. Invite form — email + role dropdown. Owner-only for admin
 *      role, everyone (owner/admin) can invite editor/viewer/advisor.
 *   3. Pending invitations — email/role/expires. Copy-link button
 *      (email delivery is backlog). Revoke button.
 *
 * Non-Owners see the member table but no dropdowns/remove buttons.
 * Owners can invite as admin/editor/viewer/advisor (Owner cannot
 * be invited — it's a promotion of an existing member).
 */

import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useWorkspace } from '../lib/workspace';
import { useAuth } from '../lib/auth';
import {
  getTeamSettings,
  createTeamInvitation,
  revokeTeamInvitation,
  approveJoinRequest as apiApproveJoinRequest,
  denyJoinRequest as apiDenyJoinRequest,
  markOrphansNotified,
  leaveWorkspace,
  updateTeamMemberRole,
  removeTeamMember,
} from '../lib/api';

type Role = 'owner' | 'admin' | 'editor' | 'viewer' | 'advisor';
type InvitableRole = Exclude<Role, 'owner'>;

interface Member {
  user_id: string;
  email: string;
  display_name: string;
  role: Role;
  joined_at: string;
}
interface PendingInvite {
  invitation_id: string;
  invited_email: string;
  role: InvitableRole;
  expires_at: string;
  created_at: string;
}
interface PendingJoinRequest {
  request_id: string;
  user_id: string;
  email: string;
  display_name: string;
  requester_ip_country: string | null;
  requested_at: string;
}
interface OrphanDomainUser {
  id: string;
  domain: string;
  user_id: string;
  email: string;
  display_name: string;
  signed_up_at: string;
  notified: boolean;
}

export function TeamSettings() {
  const { workspace, role: myRole } = useWorkspace();
  const { user: authUser } = useAuth();
  const isOwner = myRole === 'owner';
  const canManage = myRole === 'owner' || myRole === 'admin';

  const [members, setMembers] = useState<Member[] | null>(null);
  const [invites, setInvites] = useState<PendingInvite[] | null>(null);
  const [joinRequests, setJoinRequests] = useState<PendingJoinRequest[] | null>(
    null
  );
  const [orphans, setOrphans] = useState<OrphanDomainUser[] | null>(null);
  const [orphansExpanded, setOrphansExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // When a fresh invitation is created we surface the accept URL so
  // the inviter can copy-paste it. Even when email delivery succeeds
  // (email_delivery.status === 'sent') we still show the copy-link —
  // some inviters want to forward via Slack anyway, and it's the
  // definitive fallback if the invitee never sees the email.
  const [freshLink, setFreshLink] = useState<{
    email: string;
    accept_url: string;
    role: InvitableRole;
    delivery: 'sent' | 'not_configured' | 'error';
    delivery_detail?: string;
  } | null>(null);

  // Invite form state
  const [inviteEmail, setInviteEmail] = useState('');
  // V104-D-2 (BL-18): the Azure consent flow links here with ?invite_role=admin so the Azure admin is not invited as an editor.
  const [searchParams] = useSearchParams();
  const wantsAdmin = searchParams.get('invite_role') === 'admin';
  const [inviteRole, setInviteRole] = useState<InvitableRole>(wantsAdmin && isOwner ? 'admin' : 'editor');
  useEffect(() => { if (wantsAdmin && isOwner) setInviteRole('admin'); }, [wantsAdmin, isOwner]);
  // B5-2 (AR-13): "external advisor" checkbox. When checked, the invite
  // is stamped is_external=1 on the invitations row and — server-side —
  // the role is force-coerced to 'advisor' (see invitation-service.ts).
  const [inviteIsExternal, setInviteIsExternal] = useState(false);
  const ownerNames = (members ?? []).filter((m) => m.role === 'owner').map((m) => m.display_name || m.email);

  const reload = useCallback(async () => {
    setError(null);
    try {
      // V9-4-1: single composite fetch replaces 4 sequential calls.
      // Non-managers get empty arrays for invitations/join-requests/orphans
      // (server enforces the same role gate as the individual endpoints).
      const body = await getTeamSettings(workspace.slug);
      setMembers(body.members as Member[]);
      setInvites(body.invitations as PendingInvite[]);
      setJoinRequests(body.join_requests as PendingJoinRequest[]);
      setOrphans(body.orphan_domain_users as OrphanDomainUser[]);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [workspace.slug]);

  useEffect(() => {
    reload();
  }, [reload]);

  // ---- Invite -------------------------------------------------------

  const submitInvite = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!inviteEmail.trim()) return;
      setBusy(true);
      setNotice(null);
      setError(null);
      setFreshLink(null);
      try {
        const body = await createTeamInvitation(workspace.slug, {
          invited_email: inviteEmail.trim(),
          role: inviteIsExternal ? 'advisor' : inviteRole,
          is_external: inviteIsExternal,
        });
        if (!body.accept_url || !body.invitation) {
          setError(`Couldn't create invitation.`);
          return;
        }
        const delivery = body.email_delivery?.status ?? 'not_configured';
        setFreshLink({
          email: body.invitation.invited_email,
          accept_url: body.accept_url,
          role: body.invitation.role as InvitableRole,
          delivery,
          delivery_detail: body.email_delivery?.detail,
        });
        setInviteEmail('');
        // Reset the external checkbox so an "invite teammate" workflow
        // after an "invite external advisor" one doesn't silently reuse
        // the external flag. Role stays because the picker's default of
        // 'editor' is the common case.
        setInviteIsExternal(false);
        setNotice(
          delivery === 'sent'
            ? `Invitation emailed to ${body.invitation.invited_email}. Copy-link below as a backup.`
            : 'Invitation created. Copy the link below and send it to your teammate.'
        );
        await reload();
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setBusy(false);
      }
    },
    [workspace.slug, inviteEmail, inviteRole, inviteIsExternal, reload]
  );

  // ---- Revoke invitation --------------------------------------------

  const revokeInvite = useCallback(
    async (invitationId: string) => {
      setBusy(true);
      setError(null);
      try {
        await revokeTeamInvitation(workspace.slug, invitationId);
        setNotice('Invitation revoked.');
        await reload();
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setBusy(false);
      }
    },
    [workspace.slug, reload]
  );

  // ---- Approve / deny join requests ---------------------------------

  const approveJoinRequest = useCallback(
    async (requestId: string, role: InvitableRole, recognized: boolean) => {
      if (!recognized) {
        setError(
          'Tick "I recognize this person" before approving. Same email domain isn\'t the same as knowing who this is.'
        );
        return;
      }
      setBusy(true);
      setError(null);
      try {
        await apiApproveJoinRequest(workspace.slug, requestId, { role, recognized: true });
        setNotice('Join request approved. The requester is now a member.');
        await reload();
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setBusy(false);
      }
    },
    [workspace.slug, reload]
  );

  const denyJoinRequestFn = useCallback(
    async (requestId: string, email: string) => {
      if (!confirm(`Deny the join request from ${email}?`)) return;
      setBusy(true);
      setError(null);
      try {
        await apiDenyJoinRequest(workspace.slug, requestId);
        setNotice('Join request denied.');
        await reload();
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setBusy(false);
      }
    },
    [workspace.slug, reload]
  );

  // ---- Orphan-domain-users banner (B4-3) -----------------------------

  /** Pre-fill the invite form with an orphan's email; scroll to it and
   *  give a hint to click Send. Cheaper than a bulk-invite endpoint for
   *  MVP — a handful of orphans is the realistic case. */
  const inviteOrphan = useCallback((orphan: OrphanDomainUser) => {
    setInviteEmail(orphan.email);
    setInviteRole('editor');
    setNotice(
      `Pre-filled ${orphan.email}. Pick a role above and click "Send invite".`
    );
    // Best-effort scroll to the invite form. Not fatal if the anchor
    // isn't found (e.g. the section is above the fold already).
    try {
      document
        .querySelector('[aria-label="Invite teammate"]')
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } catch {
      /* no-op */
    }
  }, []);

  const dismissOrphanBanner = useCallback(async () => {
    if (!orphans || orphans.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      await markOrphansNotified(workspace.slug, orphans.map((o) => o.id));
      // Local hide — the banner only shows unnotified orphans, and the
      // server just marked them notified. On next reload the server list
      // will match; for now zero them out client-side too.
      setOrphans([]);
      setOrphansExpanded(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, [orphans, workspace.slug]);

  // ---- Leave workspace (self-service, B4-6) --------------------------

  const leaveThisWorkspace = useCallback(async () => {
    const confirmed = confirm(
      `Leave ${workspace.name}? You'll lose access immediately. If you're an Owner, you'll need to promote someone first — the server will tell you if you can't leave right now.`
    );
    if (!confirmed) return;
    setBusy(true);
    setError(null);
    try {
      await leaveWorkspace(workspace.slug);
      // Bounce out — the server just soft-removed our membership, so the
      // WorkspaceGate will 404 us on the next request anyway. Send them
      // to the app root; AppRoot's redirect logic picks a new default
      // workspace or drops them at the fork.
      window.location.href = '/';
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, [workspace.slug, workspace.name]);

  // ---- Change role ---------------------------------------------------

  const changeRole = useCallback(
    async (userId: string, role: Role) => {
      setBusy(true);
      setError(null);
      try {
        await updateTeamMemberRole(workspace.slug, userId, { role });
        setNotice('Role updated.');
        await reload();
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setBusy(false);
      }
    },
    [workspace.slug, reload]
  );

  // ---- Remove member -------------------------------------------------

  const doRemoveMember = useCallback(
    async (userId: string, email: string) => {
      if (!confirm(`Remove ${email} from this workspace?`)) return;
      setBusy(true);
      setError(null);
      try {
        await removeTeamMember(workspace.slug, userId);
        setNotice('Member removed.');
        await reload();
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setBusy(false);
      }
    },
    [workspace.slug, reload]
  );

  // ---- Render -------------------------------------------------------

  return (
    <div style={shell}>
      <h1 style={{ marginTop: 0 }}>Team</h1>

      {notice ? (
        <div role="status" style={noticeBox}>
          {notice}
        </div>
      ) : null}
      {error ? (
        <div role="alert" style={{ ...noticeBox, borderColor: 'var(--critical)', color: 'var(--critical)' }}>
          {error}
        </div>
      ) : null}

      {freshLink ? <FreshInviteLink link={freshLink} /> : null}

      {canManage && orphans && orphans.length > 0 ? (
        <OrphanDomainBanner
          orphans={orphans}
          expanded={orphansExpanded}
          onToggleExpand={() => setOrphansExpanded((x) => !x)}
          onInvite={inviteOrphan}
          onDismiss={dismissOrphanBanner}
          busy={busy}
        />
      ) : null}

      {wantsAdmin && !isOwner ? (
        <div style={noticeBox}>
          Only a workspace owner can invite an admin. Ask {ownerNames.length ? ownerNames.join(', ') : 'a workspace owner'} to invite your Microsoft admin with the Admin role.
        </div>
      ) : null}
      {wantsAdmin && isOwner ? (
        <div style={noticeBox}>
          Inviting your Microsoft admin: the role is set to Admin.
        </div>
      ) : null}

      {canManage ? (
        <InviteForm
          email={inviteEmail}
          role={inviteRole}
          isOwner={isOwner}
          isExternal={inviteIsExternal}
          workspaceName={workspace.name}
          onEmailChange={setInviteEmail}
          onRoleChange={setInviteRole}
          onExternalChange={setInviteIsExternal}
          onSubmit={submitInvite}
          busy={busy}
        />
      ) : null}

      <MembersTable
        members={members}
        isOwner={isOwner}
        canManage={canManage}
        currentUserId={authUser?.id}
        onChangeRole={changeRole}
        onRemove={doRemoveMember}
        busy={busy}
      />

      {canManage ? (
        <PendingInvitesTable
          invitations={invites}
          onRevoke={revokeInvite}
          busy={busy}
        />
      ) : null}

      {canManage ? (
        <JoinRequestsTable
          requests={joinRequests}
          busy={busy}
          onApprove={approveJoinRequest}
          onDeny={denyJoinRequestFn}
        />
      ) : null}

      <LeaveWorkspacePanel
        workspaceName={workspace.name}
        busy={busy}
        onLeave={leaveThisWorkspace}
      />
    </div>
  );
}

// ---- Fresh invite link (post-create) ---------------------------------

function FreshInviteLink({
  link,
}: {
  link: {
    email: string;
    accept_url: string;
    role: string;
    delivery: 'sent' | 'not_configured' | 'error';
    delivery_detail?: string;
  };
}) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link.accept_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      /* fall through */
    }
  };
  const headline =
    link.delivery === 'sent'
      ? `Invitation emailed to ${link.email}`
      : `Invitation ready for ${link.email}`;
  const explainer =
    link.delivery === 'sent'
      ? `We've sent the invite to ${link.email}. You can also send this link directly if they don't see the email — it's single-use, expires in 24 hours, and only accepts sign-in as ${link.email}.`
      : link.delivery === 'error'
        ? `Couldn't send the invitation email${link.delivery_detail ? ` (${link.delivery_detail})` : ''}. Copy this link and send it yourself — it's single-use and expires in 24 hours.`
        : `Email delivery isn't configured in this environment. Copy this single-use link and send it to them yourself. It expires in 24 hours and can only be used by ${link.email}.`;
  return (
    <section
      style={{
        border: '2px solid #1e8449',
        borderRadius: '0.5rem',
        padding: '1.25rem 1.5rem',
        background: 'rgba(30,132,73,0.06)',
      }}
    >
      <h2 style={{ marginTop: 0, fontSize: '1.05rem' }}>{headline}</h2>
      <p style={{ color: 'var(--text-muted)', margin: '0.35rem 0 1rem' }}>{explainer}</p>
      <div
        style={{
          display: 'flex',
          gap: '0.5rem',
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <code
          style={{
            flex: '1 1 320px',
            padding: '0.5rem 0.75rem',
            background: 'var(--surface)',
            border: '1px solid #ccc',
            borderRadius: '0.35rem',
            fontFamily: 'var(--font-mono, monospace)',
            fontSize: '0.85rem',
            wordBreak: 'break-all',
          }}
        >
          {link.accept_url}
        </code>
        <button type="button" onClick={copy} style={btnPrimary}>
          {copied ? 'Copied ✓' : 'Copy link'}
        </button>
      </div>
    </section>
  );
}

// ---- Invite form -----------------------------------------------------

function InviteForm({
  email,
  role,
  isOwner,
  onEmailChange,
  onRoleChange,
  onSubmit,
  busy,
  isExternal,
  workspaceName,
  onExternalChange,
}: {
  email: string;
  role: InvitableRole;
  isOwner: boolean;
  onEmailChange: (v: string) => void;
  onRoleChange: (v: InvitableRole) => void;
  onSubmit: (e: React.FormEvent) => void;
  busy: boolean;
  isExternal: boolean;
  workspaceName: string;
  onExternalChange: (v: boolean) => void;
}) {
  // When "external advisor" is checked, the role is force-coerced to
  // 'advisor' both here (for UI honesty — the user should see the role
  // they're granting) and server-side. Disable the picker so an admin
  // can't accidentally uncheck-and-invite as editor mid-flow.
  const effectiveRole: InvitableRole = isExternal ? 'advisor' : role;
  return (
    <section
      aria-label="Invite teammate"
      style={{
        border: '1px solid #ddd',
        borderRadius: '0.5rem',
        padding: '1.25rem 1.5rem',
        background: 'var(--surface)',
      }}
    >
      <h2 style={{ marginTop: 0, fontSize: '1.05rem' }}>Invite a teammate</h2>
      <form
        onSubmit={onSubmit}
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr auto auto',
          gap: '0.5rem',
          alignItems: 'end',
        }}
      >
        <label style={{ display: 'grid', gap: '0.25rem', fontSize: '0.85rem' }}>
          Email
          <input
            type="email"
            value={email}
            onChange={(e) => onEmailChange(e.target.value)}
            required
            placeholder="teammate@company.com"
            disabled={busy}
            style={inputStyle}
          />
        </label>
        <label style={{ display: 'grid', gap: '0.25rem', fontSize: '0.85rem' }}>
          Role
          <select
            value={effectiveRole}
            onChange={(e) => onRoleChange(e.target.value as InvitableRole)}
            disabled={busy || isExternal}
            style={inputStyle}
          >
            {isOwner ? <option value="admin">Admin</option> : null}
            <option value="editor">Editor</option>
            <option value="viewer">Viewer</option>
            <option value="advisor">Advisor</option>
          </select>
        </label>
        <button type="submit" disabled={busy || !email.trim()} style={btnPrimary}>
          {busy ? '…' : 'Send invite'}
        </button>
      </form>
      {/* B5-2 (AR-13): external-advisor checkbox. Full-width row under
          the form so its explanatory copy has room to breathe. */}
      <label
        style={{
          display: 'grid',
          gridTemplateColumns: 'auto 1fr',
          gap: '0.5rem',
          alignItems: 'start',
          marginTop: '0.75rem',
          fontSize: '0.85rem',
          color: 'var(--text)',
        }}
      >
        <input
          type="checkbox"
          checked={isExternal}
          onChange={(e) => onExternalChange(e.target.checked)}
          disabled={busy}
          style={{ marginTop: '0.2rem' }}
        />
        <span>
          This person is external to <strong>{workspaceName}</strong>.
          <br />
          <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>
            Billed at the advisor rate. MFA required on entry. They can
            access this workspace only.
          </span>
        </span>
      </label>
      <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginBottom: 0, marginTop: '0.75rem' }}>
        Only Owners can invite Admins. Owner is set by promoting an existing
        member.
      </p>
    </section>
  );
}

// ---- Members table ---------------------------------------------------

function MembersTable({
  members,
  isOwner,
  canManage,
  currentUserId,
  onChangeRole,
  onRemove,
  busy,
}: {
  members: Member[] | null;
  isOwner: boolean;
  canManage: boolean;
  currentUserId: string | undefined;
  onChangeRole: (userId: string, role: Role) => void;
  onRemove: (userId: string, email: string) => void;
  busy: boolean;
}) {
  return (
    <section
      aria-label="Active members"
      style={{
        border: '1px solid #ddd',
        borderRadius: '0.5rem',
        background: 'var(--surface)',
        overflow: 'hidden',
      }}
    >
      <h2 style={{ margin: 0, padding: '1rem 1.25rem', fontSize: '1.05rem' }}>
        Members {members ? `(${members.length})` : ''}
      </h2>
      {members == null ? (
        <div style={{ padding: '1.25rem', color: 'var(--text-muted)' }}>Loading…</div>
      ) : (
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '0.9rem',
          }}
        >
          <thead>
            <tr style={{ background: 'var(--surface-elevated)', textAlign: 'left' }}>
              <th style={cellStyle}>Name / Email</th>
              <th style={cellStyle}>Role</th>
              <th style={cellStyle}>Joined</th>
              {canManage ? <th style={cellStyle}></th> : null}
            </tr>
          </thead>
          <tbody>
            {members.map((m) => (
              <tr key={m.user_id} style={{ borderTop: '1px solid #eee' }}>
                <td style={cellStyle}>
                  <div style={{ fontWeight: 500 }}>{m.display_name}</div>
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>{m.email}</div>
                </td>
                <td style={cellStyle}>
                  {isOwner && m.user_id !== currentUserId ? (
                    <select
                      value={m.role}
                      onChange={(e) => onChangeRole(m.user_id, e.target.value as Role)}
                      disabled={busy}
                      style={{ ...inputStyle, padding: '0.35rem 0.5rem' }}
                    >
                      <option value="owner">Owner</option>
                      <option value="admin">Admin</option>
                      <option value="editor">Editor</option>
                      <option value="viewer">Viewer</option>
                      <option value="advisor">Advisor</option>
                    </select>
                  ) : (
                    <span>{m.role}</span>
                  )}
                </td>
                <td style={{ ...cellStyle, color: 'var(--text-muted)' }}>
                  {new Date(m.joined_at).toLocaleDateString()}
                </td>
                {canManage ? (
                  <td style={{ ...cellStyle, textAlign: 'right' }}>
                    {m.user_id !== currentUserId ? (
                      <button
                        type="button"
                        onClick={() => onRemove(m.user_id, m.email)}
                        disabled={busy}
                        style={btnGhostSmall}
                      >
                        Remove
                      </button>
                    ) : (
                      <span style={{ color: 'var(--text-dim)', fontSize: '0.8rem' }}>you</span>
                    )}
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

// ---- Pending invitations table ---------------------------------------

function PendingInvitesTable({
  invitations,
  onRevoke,
  busy,
}: {
  invitations: PendingInvite[] | null;
  onRevoke: (id: string) => void;
  busy: boolean;
}) {
  return (
    <section
      aria-label="Pending invitations"
      style={{
        border: '1px solid #ddd',
        borderRadius: '0.5rem',
        background: 'var(--surface)',
        overflow: 'hidden',
      }}
    >
      <h2 style={{ margin: 0, padding: '1rem 1.25rem', fontSize: '1.05rem' }}>
        Pending invitations {invitations ? `(${invitations.length})` : ''}
      </h2>
      {invitations == null ? (
        <div style={{ padding: '1.25rem', color: 'var(--text-muted)' }}>Loading…</div>
      ) : invitations.length === 0 ? (
        <div style={{ padding: '1.25rem', color: 'var(--text-muted)' }}>
          No pending invitations.
        </div>
      ) : (
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '0.9rem',
          }}
        >
          <thead>
            <tr style={{ background: 'var(--surface-elevated)', textAlign: 'left' }}>
              <th style={cellStyle}>Email</th>
              <th style={cellStyle}>Role</th>
              <th style={cellStyle}>Expires</th>
              <th style={cellStyle}></th>
            </tr>
          </thead>
          <tbody>
            {invitations.map((i) => (
              <tr key={i.invitation_id} style={{ borderTop: '1px solid #eee' }}>
                <td style={cellStyle}>{i.invited_email}</td>
                <td style={cellStyle}>{i.role}</td>
                <td style={{ ...cellStyle, color: 'var(--text-muted)' }}>
                  {new Date(i.expires_at).toLocaleString()}
                </td>
                <td style={{ ...cellStyle, textAlign: 'right' }}>
                  <button
                    type="button"
                    onClick={() => onRevoke(i.invitation_id)}
                    disabled={busy}
                    style={btnGhostSmall}
                  >
                    Revoke
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

// ---- Orphan-domain-users banner (B4-3) ------------------------------

function OrphanDomainBanner({
  orphans,
  expanded,
  onToggleExpand,
  onInvite,
  onDismiss,
  busy,
}: {
  orphans: OrphanDomainUser[];
  expanded: boolean;
  onToggleExpand: () => void;
  onInvite: (orphan: OrphanDomainUser) => void;
  onDismiss: () => void;
  busy: boolean;
}) {
  // Group by domain for the summary line — most workspaces claim one
  // domain, but this handles multi-claim gracefully.
  const byDomain = new Map<string, OrphanDomainUser[]>();
  for (const o of orphans) {
    const bucket = byDomain.get(o.domain) ?? [];
    bucket.push(o);
    byDomain.set(o.domain, bucket);
  }
  const summary = Array.from(byDomain.entries())
    .map(([d, list]) => `${list.length} @${d}`)
    .join(', ');

  return (
    <section
      aria-label="Orphan domain users"
      style={{
        border: '1px solid #d3b100',
        borderRadius: '0.5rem',
        background: 'rgba(255, 204, 0, 0.08)',
        padding: '1rem 1.25rem',
        display: 'grid',
        gap: '0.75rem',
      }}
    >
      <div
        style={{
          display: 'flex',
          gap: '1rem',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ flex: 1, minWidth: 220 }}>
          <strong>{summary}</strong> signed up with an email on your claimed
          domain but aren't in this workspace.
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            type="button"
            onClick={onToggleExpand}
            style={btnGhostSmall}
            disabled={busy}
          >
            {expanded ? 'Hide' : 'Show'}
          </button>
          <button
            type="button"
            onClick={onDismiss}
            style={btnGhostSmall}
            disabled={busy}
            title="Mark them as seen — they'll stay in the audit log, and the banner won't reappear for these users."
          >
            Dismiss
          </button>
        </div>
      </div>
      {expanded ? (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: '0.5rem' }}>
          {orphans.map((o) => (
            <li
              key={o.id}
              style={{
                display: 'flex',
                gap: '0.75rem',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '0.5rem 0.75rem',
                background: 'var(--surface)',
                borderRadius: '0.35rem',
                border: '1px solid #eee',
              }}
            >
              <div>
                <div style={{ fontWeight: 500 }}>
                  {o.display_name || o.email}
                </div>
                <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                  {o.email} · signed up{' '}
                  {new Date(o.signed_up_at).toLocaleDateString()}
                </div>
              </div>
              <button
                type="button"
                onClick={() => onInvite(o)}
                disabled={busy}
                style={btnGhostSmall}
              >
                Invite
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

// ---- Join requests queue (B4-2) -------------------------------------

function JoinRequestsTable({
  requests,
  busy,
  onApprove,
  onDeny,
}: {
  requests: PendingJoinRequest[] | null;
  busy: boolean;
  onApprove: (requestId: string, role: InvitableRole, recognized: boolean) => void;
  onDeny: (requestId: string, email: string) => void;
}) {
  return (
    <section
      aria-label="Join requests"
      style={{
        border: '1px solid #ddd',
        borderRadius: '0.5rem',
        background: 'var(--surface)',
        overflow: 'hidden',
      }}
    >
      <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid #eee' }}>
        <h2 style={{ margin: 0, fontSize: '1.05rem' }}>
          Join requests {requests ? `(${requests.length})` : ''}
        </h2>
        <p style={{ margin: '0.25rem 0 0', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
          People who signed up with an email on your claimed domain and asked to
          join. Approvals are irreversible from the queue — the requester
          becomes a member immediately.
        </p>
      </div>
      {requests == null ? (
        <div style={{ padding: '1.25rem', color: 'var(--text-muted)' }}>Loading…</div>
      ) : requests.length === 0 ? (
        <div style={{ padding: '1.25rem', color: 'var(--text-muted)' }}>
          No pending join requests.
        </div>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {requests.map((r) => (
            <JoinRequestRow
              key={r.request_id}
              request={r}
              busy={busy}
              onApprove={onApprove}
              onDeny={onDeny}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function JoinRequestRow({
  request,
  busy,
  onApprove,
  onDeny,
}: {
  request: PendingJoinRequest;
  busy: boolean;
  onApprove: (requestId: string, role: InvitableRole, recognized: boolean) => void;
  onDeny: (requestId: string, email: string) => void;
}) {
  const [role, setRole] = useState<InvitableRole>('editor');
  const [recognized, setRecognized] = useState(false);
  const requestedAt = new Date(request.requested_at);
  return (
    <li
      style={{
        display: 'grid',
        gap: '0.75rem',
        padding: '1rem 1.25rem',
        borderTop: '1px solid #eee',
      }}
    >
      <div>
        <div style={{ fontWeight: 500 }}>{request.display_name || request.email}</div>
        <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
          {request.email}
          {request.requester_ip_country
            ? ` · signed up from ${request.requester_ip_country}`
            : ''}
          {' · requested '}
          {requestedAt.toLocaleString()}
        </div>
      </div>
      <label
        style={{
          display: 'flex',
          gap: '0.5rem',
          alignItems: 'flex-start',
          fontSize: '0.9rem',
        }}
      >
        <input
          type="checkbox"
          checked={recognized}
          onChange={(e) => setRecognized(e.target.checked)}
          disabled={busy}
          style={{ marginTop: '0.2rem' }}
        />
        <span>
          I recognize this person and want to add them to the workspace. A
          matching email domain isn't proof of identity — only tick this if you
          actually know who they are.
        </span>
      </label>
      <div
        style={{
          display: 'flex',
          gap: '0.75rem',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <label
          style={{
            display: 'flex',
            gap: '0.35rem',
            alignItems: 'center',
            fontSize: '0.85rem',
          }}
        >
          Role
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as InvitableRole)}
            disabled={busy}
            style={{ ...inputStyle, padding: '0.35rem 0.5rem' }}
          >
            <option value="editor">Editor</option>
            <option value="viewer">Viewer</option>
            <option value="advisor">Advisor</option>
          </select>
        </label>
        <button
          type="button"
          onClick={() => onApprove(request.request_id, role, recognized)}
          disabled={busy || !recognized}
          style={btnPrimary}
          title={
            recognized
              ? 'Approve and add as a member'
              : 'Tick "I recognize this person" first'
          }
        >
          Approve
        </button>
        <button
          type="button"
          onClick={() => onDeny(request.request_id, request.email)}
          disabled={busy}
          style={btnGhostSmall}
        >
          Deny
        </button>
      </div>
    </li>
  );
}

// ---- Leave workspace (B4-6) -----------------------------------------

function LeaveWorkspacePanel({
  workspaceName,
  busy,
  onLeave,
}: {
  workspaceName: string;
  busy: boolean;
  onLeave: () => void;
}) {
  return (
    <section
      aria-label="Leave this workspace"
      style={{
        border: '1px solid #d9534f',
        borderRadius: '0.5rem',
        padding: '1rem 1.25rem',
        background: 'rgba(217, 83, 79, 0.04)',
        display: 'grid',
        gap: '0.5rem',
      }}
    >
      <h2 style={{ margin: 0, fontSize: '1.05rem', color: 'var(--critical)' }}>
        Leave this workspace
      </h2>
      <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.9rem' }}>
        Removes you from {workspaceName}. You'll lose access immediately and
        won't see this workspace in your switcher. If you're the only Owner,
        you'll need to promote another member to Owner first — the server will
        refuse and tell you.
      </p>
      <div>
        <button
          type="button"
          onClick={onLeave}
          disabled={busy}
          style={{
            ...btnGhostSmall,
            borderColor: 'var(--critical)',
            color: 'var(--critical)',
          }}
        >
          Leave workspace
        </button>
      </div>
    </section>
  );
}

// ---- Style tokens ---------------------------------------------------

const shell: React.CSSProperties = {
  maxWidth: 900,
  margin: '0 auto',
  padding: '2rem',
  display: 'grid',
  gap: '1.5rem',
  fontFamily: 'var(--font-ui, system-ui)',
};

const noticeBox: React.CSSProperties = {
  padding: '0.75rem 1rem',
  border: '1px solid var(--border)',
  background: 'var(--surface-elevated)',
  borderRadius: '0.35rem',
  color: 'var(--text)',
};

const cellStyle: React.CSSProperties = {
  padding: '0.65rem 1rem',
  verticalAlign: 'top',
};

const inputStyle: React.CSSProperties = {
  padding: '0.55rem 0.75rem',
  border: '1px solid var(--border-strong)',
  background: 'var(--surface-elevated)',
  color: 'var(--text)',
  borderRadius: '0.35rem',
  fontSize: '0.95rem',
  minWidth: 140,
};

// PR-P0-1c · Migrated to Direction B CTA vocabulary. Uses the near-
// black --cta-bg (Direction B) instead of the legacy amber CTA
// background. Consumers should ideally use `<Button variant="primary">`
// from the DS; this local style is retained for the specific TeamSettings
// layout that needs custom padding + border-radius mid-refactor.
const btnPrimary: React.CSSProperties = {
  appearance: 'none',
  padding: '0.6rem 1rem',
  borderRadius: '0.35rem',
  border: 0,
  background: 'var(--cta-bg)',
  color: 'var(--cta-text)',
  fontSize: '0.9rem',
  fontWeight: 500,
  cursor: 'pointer',
};

const btnGhostSmall: React.CSSProperties = {
  appearance: 'none',
  padding: '0.4rem 0.75rem',
  borderRadius: '0.35rem',
  border: '1px solid var(--border-strong)',
  background: 'var(--surface)',
  color: 'var(--text)',
  fontSize: '0.85rem',
  cursor: 'pointer',
};
